// Metadata-driven register pages: list + create/edit form + delete + CSV export.
import React, { useEffect, useState } from 'react';
import { api, saveWithOverride, download, ApiError, currentUser } from './api';
import { DataTable, Modal, Field, DateField, ErrorBox, Confirm, StatusChip, Column, Spinner } from './ui';
import { toAus, statusLabel } from './util';

export type FieldSpec = {
  name: string;
  label: string;
  type: 'text' | 'date' | 'int' | 'real' | 'bool' | 'select' | 'textarea' | 'email';
  options?: string[] | (() => Promise<{ value: any; label: string }[]>);
  lookup?: string;     // entity to load options from; stores the numeric id
  nameLookup?: string; // entity to load options from; stores the name as text
  required?: boolean;
  default?: any;
  section?: string;
  sensitive?: boolean; // hidden on list views
};

// Options sourced from a maintained list (roles, trades) where the *name* is stored.
export function useNameLookup(entity: string) {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => {
    api('/' + entity).then(setRows).catch(() => setRows([]));
  }, [entity]);
  return {
    rows,
    options: rows.filter(r => r.active !== 0).map(r => ({ value: r.name, label: r.name }))
  };
}

export function useLookup(entity: string | null, labelFn?: (r: any) => string) {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => {
    if (!entity) return;
    api('/' + entity).then(setRows).catch(() => setRows([]));
  }, [entity]);
  const label = labelFn || ((r: any) => r.name || `${r.preferred_name || r.first_name || ''} ${r.last_name || ''}`.trim() || `#${r.id}`);
  return { rows, options: rows.map(r => ({ value: r.id, label: label(r) })), byId: Object.fromEntries(rows.map(r => [r.id, label(r)])) as Record<number, string> };
}

// A dropdown that also keeps whatever value the record already holds selectable, even if
// that value is no longer one of the standard options. Without this, editing an older
// record (e.g. a roster pattern we have since retired) would show a blank box and saving
// would silently erase the field.
function SelectField({ spec, value, lookups, onChange }: {
  spec: FieldSpec;
  value: any;
  lookups: Record<string, { value: any; label: string }[]>;
  onChange: (v: any) => void;
}) {
  const options =
    spec.lookup ? (lookups[spec.lookup] || [])
    : spec.nameLookup ? (lookups[spec.nameLookup] || [])
    : ((spec.options as string[]) || []).map(o => ({ value: o, label: statusLabel(o) }));

  const hasValue = value !== null && value !== undefined && value !== '';
  const isRetired = hasValue && !options.some(o => String(o.value) === String(value));

  return (
    <select
      className="input"
      value={value ?? ''}
      onChange={e => onChange(e.target.value === '' ? null : (spec.lookup ? Number(e.target.value) : e.target.value))}
    >
      <option value="">—</option>
      {isRetired && <option value={String(value)}>{String(value)} (existing)</option>}
      {options.map(o => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function FormFields({ specs, values, onChange, lookups }: {
  specs: FieldSpec[];
  values: any;
  onChange: (name: string, value: any) => void;
  lookups: Record<string, { value: any; label: string }[]>;
}) {
  const sections: Record<string, FieldSpec[]> = {};
  for (const s of specs) (sections[s.section || ''] ||= []).push(s);
  return (
    <>
      {Object.entries(sections).map(([section, fields]) => (
        <div key={section}>
          {section && <h4 className="form-section">{section}</h4>}
          <div className="form-grid">
            {fields.map(spec => (
              <Field key={spec.name} label={spec.label + (spec.required ? ' *' : '')}>
                {spec.type === 'date' ? (
                  <DateField value={values[spec.name]} onChange={v => onChange(spec.name, v)} required={spec.required} />
                ) : spec.type === 'bool' ? (
                  <input type="checkbox" className="checkbox" checked={!!values[spec.name]}
                    onChange={e => onChange(spec.name, e.target.checked ? 1 : 0)} />
                ) : spec.type === 'select' || spec.lookup || spec.nameLookup ? (
                  <SelectField spec={spec} value={values[spec.name]} lookups={lookups}
                    onChange={v => onChange(spec.name, v)} />
                ) : spec.type === 'textarea' ? (
                  <textarea className="input" rows={3} value={values[spec.name] ?? ''}
                    onChange={e => onChange(spec.name, e.target.value)} />
                ) : (
                  <input className="input"
                    type={spec.type === 'int' || spec.type === 'real' ? 'number' : spec.type === 'email' ? 'email' : 'text'}
                    step={spec.type === 'real' ? '0.01' : undefined}
                    required={spec.required}
                    value={values[spec.name] ?? ''}
                    onChange={e => onChange(spec.name, e.target.value)} />
                )}
              </Field>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

export function RegisterPage({ title, entity, specs, columns, lookupEntities, extraToolbar, onRowExtra, rowTransform, canDelete }: {
  title: string;
  entity: string;
  specs: FieldSpec[];
  columns: Column<any>[];
  // Accepts both useLookup (id-valued) and useNameLookup (name-valued) results.
  lookupEntities?: Record<string, { options: { value: any; label: string }[] }>;
  extraToolbar?: React.ReactNode;
  onRowExtra?: (row: any) => React.ReactNode;
  rowTransform?: (rows: any[]) => any[];
  canDelete?: boolean;
}) {
  const [rows, setRows] = useState<any[] | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => api('/' + entity).then(r => setRows(rowTransform ? rowTransform(r) : r)).catch(e => setErrors([e.message]));
  useEffect(() => { load(); }, [entity]);

  const lookups: Record<string, { value: any; label: string }[]> = {};
  for (const [k, v] of Object.entries(lookupEntities || {})) lookups[k] = v.options;

  const startNew = () => {
    const v: any = {};
    for (const s of specs) if (s.default !== undefined) v[s.name] = s.default;
    setErrors([]);
    setEditing({ __new: true, ...v });
  };

  const save = async () => {
    setSaving(true);
    setErrors([]);
    const body: any = {};
    for (const s of specs) if (editing[s.name] !== undefined) body[s.name] = editing[s.name];
    try {
      if (editing.__new) await saveWithOverride('/' + entity, 'POST', body);
      else await saveWithOverride(`/${entity}/${editing.id}`, 'PUT', body);
      setEditing(null);
      load();
    } catch (e: any) {
      setErrors(e instanceof ApiError && e.errors.length ? e.errors : [e.message]);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api(`/${entity}/${confirmDelete.id}`, { method: 'DELETE' });
      setConfirmDelete(null);
      setEditing(null);
      load();
    } catch (e: any) {
      setConfirmDelete(null);
      setErrors([e.message]);
    }
  };

  const readonly = currentUser?.role === 'readonly';

  if (!rows) return <Spinner />;
  return (
    <div className="page">
      <div className="page-head">
        <h2>{title}</h2>
        <div className="btn-row">
          {extraToolbar}
          <button className="btn" onClick={() => download(`/export/csv/${entity}`)}>Export CSV</button>
          {!readonly && <button className="btn btn-primary" onClick={startNew}>+ Add</button>}
        </div>
      </div>
      <ErrorBox error={errors} onDismiss={() => setErrors([])} />
      <DataTable columns={columns} rows={rows} onRowClick={readonly ? undefined : r => { setErrors([]); setEditing({ ...r }); }} />

      {editing && (
        <Modal title={editing.__new ? `Add ${title.replace(/s$/, '').toLowerCase()}` : `Edit #${editing.id}`} onClose={() => setEditing(null)} wide>
          <ErrorBox error={errors} onDismiss={() => setErrors([])} />
          <FormFields specs={specs} values={editing} lookups={lookups}
            onChange={(n, v) => setEditing((prev: any) => ({ ...prev, [n]: v }))} />
          {onRowExtra && !editing.__new && onRowExtra(editing)}
          <div className="btn-row form-actions">
            <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
            {!editing.__new && (canDelete ?? currentUser?.role === 'admin') && (
              <button className="btn btn-danger" onClick={() => setConfirmDelete(editing)}>Delete</button>
            )}
            <button className="btn" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </Modal>
      )}
      {confirmDelete && (
        <Confirm
          message={`Delete record #${confirmDelete.id}? This cannot be undone.\nConsider marking it inactive or cancelled instead to keep history.`}
          onYes={doDelete} onNo={() => setConfirmDelete(null)} />
      )}
    </div>
  );
}

export const dateCol = (key: string, label: string): Column<any> => ({
  key, label, render: r => toAus(r[key]), sortValue: r => r[key]
});
export const statusCol = (key = 'status'): Column<any> => ({
  key, label: 'Status', render: r => <StatusChip status={r[key]} />, sortValue: r => r[key]
});
export const boolCol = (key: string, label: string): Column<any> => ({
  key, label, render: r => (r[key] ? 'Yes' : ''), sortValue: r => r[key]
});
